import React, { createContext, useContext, useEffect, useState } from "react";
import {
  AppUpdate,
  AppUpdateAvailability,
} from "@capawesome/capacitor-app-update";
import { Capacitor } from '@capacitor/core';
import { actions } from "../../Redux/store";

interface AppUpdateContextType {
  checkForUpdate: () => Promise<void>;
  isUpdateAvailable: boolean;
  isChecking: boolean;
  error: string | null;
}

const AppUpdateContext = createContext<AppUpdateContextType | undefined>(undefined);

export const useAppUpdate = () => {
  const context = useContext(AppUpdateContext);
  if (!context) {
    throw new Error("useAppUpdate must be used within an AppUpdateProvider");
  }
  return context;
};

export const AppUpdateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkForUpdate = async () => {
    setIsChecking(true);
    setError(null);
    
    try {
      const { updateAvailability } = await AppUpdate.getAppUpdateInfo();
      
      if (updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        console.log("Update available");
        setIsUpdateAvailable(false);
        // Show update modal for both platforms
        const platform = Capacitor.getPlatform();
        if (platform === 'android' || platform === 'ios') {
          actions.modal.openAppUpdateModal(null);
        } else {
          console.log("Updates not supported on this platform");
        }
      } else {
        console.log("App is up to date");
        setIsUpdateAvailable(false);
      }
    } catch (err) {
      console.error("Error checking for updates:", err);
      setError("Failed to check for updates");
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkForUpdate();
  }, []);

  const value: AppUpdateContextType = {
    checkForUpdate,
    isUpdateAvailable,
    isChecking,
    error,
  };

  return (
    <AppUpdateContext.Provider value={value}>
      {children}
    </AppUpdateContext.Provider>
  );
};
